import { Link } from 'react-router-dom';
import { MessageSquare, Heart, ChevronRight, Sparkles, Calendar } from 'lucide-react';
import { useForumCategories } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import SEO from '@/components/SEO';

export default function ForumHomePage() {
  const { data: categories = [], isLoading } = useForumCategories();
  const { isAuthenticated } = useAuthStore();

  // Build parent→children tree
  const parents = categories.filter((c: any) => !c.parent_id);
  const childrenOf = (parentId: string) => categories.filter((c: any) => c.parent_id === parentId);

  // Find weekly thread links
  const weeklyPulls = categories.find((c: any) => c.slug === 'weekly-pulls');
  const weeklyTrades = categories.find((c: any) => c.slug === 'weekly-trades');

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        url="/forum"
        description="Join the Open Card Vault community forum. Discuss cards, share your pulls, find trades, and connect with collectors worldwide."
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-10">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="h-8 w-8" />
            <h1 className="text-4xl font-display font-bold">Community Forum</h1>
          </div>
          <p className="text-brand-100">Discuss cards, share pulls, find trades</p>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Quick links */}
        <div className="flex flex-wrap gap-3 mb-8">
          {weeklyPulls && (
            <Link to={`/forum/${weeklyPulls.slug}`}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:border-brand-400 hover:text-brand-600 dark:text-brand-400 shadow-sm transition-colors">
              <Sparkles className="h-4 w-4 text-yellow-500" />
              Weekly Pull Thread
            </Link>
          )}
          {weeklyTrades && (
            <Link to={`/forum/${weeklyTrades.slug}`}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-sm font-medium text-gray-700 dark:text-zinc-300 hover:border-brand-400 hover:text-brand-600 dark:text-brand-400 shadow-sm transition-colors">
              <Calendar className="h-4 w-4 text-blue-500" />
              Weekly Trade Thread
            </Link>
          )}
          {isAuthenticated && (
            <Link to="/forum/wishlist-matches"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 dark:bg-pink-900/20 border border-pink-200 text-sm font-medium text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:bg-pink-900/40 shadow-sm transition-colors">
              <Heart className="h-4 w-4 fill-pink-500 text-pink-500" />
              My Wishlist Matches
            </Link>
          )}
        </div>

        {/* Category tree */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {parents.map((parent: any) => (
              <div key={parent.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
                {/* Parent category header */}
                <Link to={`/forum/${parent.slug}`}
                  className="flex items-center justify-between p-5 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors group">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-gray-900 dark:text-zinc-100 group-hover:text-brand-600 dark:text-brand-400 transition-colors">
                      {parent.name}
                    </h2>
                    {parent.description && (
                      <p className="text-sm text-gray-500 dark:text-zinc-500 mt-0.5">{parent.description}</p>
                    )}
                    {parent.latest_thread_title && (
                      <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1 truncate">
                        Latest: {parent.latest_thread_title}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-semibold text-gray-700 dark:text-zinc-300">{parent.thread_count ?? 0}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500">threads</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-500 dark:text-zinc-500 group-hover:text-brand-500 transition-colors" />
                  </div>
                </Link>

                {/* Sub-categories */}
                {childrenOf(parent.id).length > 0 && (
                  <div className="border-t border-gray-50">
                    {childrenOf(parent.id).map((child: any) => (
                      <Link key={child.id} to={`/forum/${child.slug}`}
                        className="flex items-center justify-between px-5 py-3 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors group border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-zinc-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-gray-700 dark:text-zinc-300 group-hover:text-brand-600 dark:text-brand-400 transition-colors">
                              {child.name}
                            </span>
                            {child.latest_thread_title && (
                              <p className="text-xs text-gray-500 dark:text-zinc-500 truncate mt-0.5">
                                {child.latest_thread_title}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-zinc-500 flex-shrink-0 ml-3">{child.thread_count ?? 0} threads</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
